/**
 * Bulk action bar shown above the ASN grid when one or more in-transit ASNs
 * are selected. Offers "Mark Delivered" (calls the per-ASN endpoint for each
 * selection) and "Clear". Extracted from {@link ASNListTab} to keep that file
 * under the 500-line limit.
 */
import { Button } from '../../ui/button';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';

interface ASNBulkActionBarProps {
  /** Number of currently selected ASNs. */
  selectedCount: number;
  /** True while the bulk mark-delivered run is in flight. */
  marking: boolean;
  /** Clear the current selection. */
  onClear: () => void;
  /** Mark all selected ASNs as delivered. */
  onMarkDelivered: () => void;
}

/**
 * Selection summary + bulk actions for in-transit ASNs.
 *
 * @param props.selectedCount - how many ASNs are selected
 * @param props.marking - whether a mark-delivered run is in progress
 * @param props.onClear - clear the selection
 * @param props.onMarkDelivered - run the bulk mark-delivered action
 * @returns the action bar, or null when nothing is selected
 */
export function ASNBulkActionBar({
  selectedCount,
  marking,
  onClear,
  onMarkDelivered,
}: ASNBulkActionBarProps) {
  if (selectedCount === 0) return null;
  return (
    <Card className="border-blue-200 bg-blue-50/60">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-blue-900">
            {selectedCount} In Transit ASN{selectedCount === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClear} disabled={marking}>
              <X className="h-4 w-4 mr-1.5" />
              Clear
            </Button>
            <Button
              size="sm"
              onClick={onMarkDelivered}
              disabled={marking}
              data-tour="asn-bulk-mark-delivered"
            >
              {marking ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
              )}
              Mark Delivered
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
